import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import VmiPageTypes from "@insite/client-framework/Types/VmiPageTypes";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@insite/mobius/Breadcrumbs";
import { LinkProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
    };
};

interface OwnProps {
    extendedStyles?: PageBreadcrumbsStyles;
    theme?: THEMES;
}

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
}

export enum THEMES {
    DARK = 1,
    LIGHT = 2,
    GRAY = 3,
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const homePageLink = { children: translate("Home"), href: "/" };

const PageBreadcrumbs: FC<Props> = (props: Props) => {
    let links = props.links || generateLinksFrom(props.linksState, props.nodeId);
    if (
        links.length > 0 &&
        links[0].href !== homePageLink.href &&
        links.some(o => o.type === "HomePage" || o.type === undefined)
    ) {
        links = [homePageLink, ...links];
    }

    const themes: { [key in THEMES]?: string } = {
        [THEMES.DARK]: "black",
        [THEMES.LIGHT]: "white",
        [THEMES.GRAY]: UCSColors.gray03,
    };

    const customProps: TypographyPresentationProps = {
        color: themes[props.theme || THEMES.LIGHT],
        size: 14,
        css: css`
            padding-top: 4px;
            color: ${themes[props.theme || THEMES.LIGHT]} !important;
        `,
    };

    const themedPropss: TypographyPresentationProps = {
        ...pageBreadcrumbStyles.breadcrumbs,
        css: css`
            padding-top: 4px;
        `,
    };

    const styles = useMergeStyles("pageBreadcrumbs", pageBreadcrumbStyles, props.extendedStyles);
    return (
        <Breadcrumbs
            links={links}
            data-test-selector="pageBreadcrumbs"
            typographyProps={props.theme ? customProps : themedPropss}
            {...styles.breadcrumbs}
        />
    );
};
export function generateLinksFrom(linksState: LinksState, nodeId: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);
    const vmiExcludePageTypes: string[] = ["HomePage", "MyAccountPage"];
    const search = (targetText: string) => (sourceTextArray: string) => sourceTextArray === targetText;
    const isVmiPageType = !currentLink?.type ? false : VmiPageTypes.some(search(currentLink.type));

    while (currentLink) {
        const isVmiExcludePageType = !currentLink?.type ? false : vmiExcludePageTypes.some(search(currentLink.type));

        if (!isVmiPageType || !isVmiExcludePageType) {
            const result = links.find(obj => {
                return obj.children?.toString() === currentLink?.title;
            });
            if (!result) {
                links.unshift({ children: currentLink.title, href: currentLink.url, type: currentLink.type });
            }
        }
        if (currentLink.type === "HomePage") {
            const result = links.find(obj => {
                return obj.children?.toString() === currentLink?.title;
            });
            if (!result) {
                links.unshift(homePageLink);
            }
        } else {
            const result = links.find(obj => {
                return obj.children?.toString() === currentLink?.title;
            });
            if (!result) {
                links.unshift({ children: currentLink.title, href: currentLink.url });
            }
        }
        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }
    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
